import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { deleteWithUndo } from '../lib/undoDelete'
import { useAuth } from '../hooks/useAuth'
import { generateRota, verifyRota, absencesFrom } from '../lib/dutyRota'
import { fetchRows } from '../lib/api'
import { todayLocal } from '../lib/dates'
import { shipTodayAuto } from '../lib/shipTime'
import DateField from '../components/DateField'
import html2pdf from 'html2pdf.js'
import { escHtml } from '../lib/escHtml'

const gv = v => (!v ? '' : (typeof v === 'object' && v.value) ? v.value : String(v))
const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const DEFAULT_NOTES = [
  { title: 'General', body: 'Follow the "Off charter watch Duties" checklist. Crew member is on watch from 07:45 to 07:45 the next day. You hold overall responsibility for the yacht. You must respond to all alarms.' },
  { title: 'Fire Watch', body: 'Ensure all external and internal lighting, electrical appliances and power tools are switched off. Familiarise yourself with the fire plan and muster list in advance.' },
  { title: 'Security / Safety', body: 'Ensure all doors and hatches are closed and locked. Be aware of contractors and crew members on board. Patrol before and after the working day and at 21:00. Every 4h on weekends.' },
  { title: 'Cleanliness and Uniform', body: 'Be present on watch in a clean uniform and with your radio. Ensure the dishwasher and garbage bins are emptied, the sink is clean and the area vacuumed. Leave minimum interior lighting on.' },
]

// Heads of department, decided from the role string - NOT by the model,
// which classifies 1st Officer as a non-head about half the time.
const isHOD = role => /chief|1st officer|first officer|head of/i.test(role || '')
const shortName = (full) => {
  if (!full) return ''
  const parts = full.trim().split(/\s+/)
  if (parts.length === 1) return parts[0]
  return parts[0] + ' ' + parts[1][0] + '.'
}
const isCaptain = role => /captain|master/i.test(role || '')

export default function DutyRota() {
  const { config } = useAuth()
  const [crew, setCrew] = useState([])
  const [allCrew, setAllCrew] = useState([])
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [spec, setSpec] = useState(null)
  const [rota, setRota] = useState(null)
  const [verdict, setVerdict] = useState(null)
  const [archive, setArchive] = useState([])
  const [showArchive, setShowArchive] = useState(false)
  const [saving, setSaving] = useState(false)
  const [movements, setMovements] = useState([])
  const [showMoves, setShowMoves] = useState(false)
  const [notes, setNotes] = useState(DEFAULT_NOTES)

  async function loadMovements() {
    const { data } = await supabase.from('crew_movements').select('*')
      .eq('client_id', config.id).order('sign_off', { ascending: true })
    setMovements(data || [])
  }
  useEffect(() => { if (config?.id) loadMovements() }, [config?.id])

  async function addMovement(m) {
    const { error } = await supabase.from('crew_movements').insert({ client_id: config.id, ...m })
    if (error) alert('Could not save: ' + error.message)
    else loadMovements()
  }
  async function delMovement(m) {
    const label = 'crew movement ' + [m.crew_name, m.sign_on || m.sign_off].filter(Boolean).join(' ')
    if (!window.confirm('Delete the ' + label + '? You can undo it for 10 seconds.')) return
    try { await deleteWithUndo(supabase, { table: 'crew_movements', id: m.id, label, onUndone: () => loadMovements() }) } catch (e) { alert('Could not delete: ' + (e.message || e)) }
    loadMovements()
  }

  useEffect(() => {
    if (!config?.id) return
    ;(async () => {
      try {
        const rows = await fetchRows('crew')
        const everyone = (rows || []).map(r => ({
          id: r.id,
          name: (gv(r['First Name']) + ' ' + gv(r['Last Name'])).trim(),
          role: gv(r['Position']) || gv(r['Role']) || '',
          onboard: r['Onboard'] === true || r['Onboard'] === 'true',
        })).filter(c => c.name)
        setAllCrew(everyone)
        setCrew(everyone.filter(c => c.onboard))
      } catch (e) { /* optional */ }
    })()
  }, [config?.id])

  async function loadArchive() {
    const { data } = await supabase.from('duty_rotas').select('*')
      .eq('client_id', config.id).order('created_at', { ascending: false }).limit(30)
    setArchive(data || [])
  }
  useEffect(() => { if (config?.id) loadArchive() }, [config?.id])

  // Handoff from the AI Assistant. The brain never builds the rota - it passes the
  // request here, where the one true builder lives (code-side HOD/Captain overrides
  // + generateRota + verifyRota). Wait for crew AND movements before firing, or the
  // rota would be generated against an empty crew list.
  const [handoff, setHandoff] = useState(() => {
    try { return localStorage.getItem('hc_rota_request') || '' } catch { return '' }
  })
  useEffect(() => {
    if (!handoff || !crew.length || busy) return
    try { localStorage.removeItem('hc_rota_request') } catch {}
    setText(handoff)
    setHandoff('')
  }, [handoff, crew.length]) // eslint-disable-line

  // Fire ask() only once the text state has actually landed.
  const [autoFired, setAutoFired] = useState(false)
  useEffect(() => {
    if (autoFired || !text || !crew.length || busy) return
    let pending = false
    try { pending = !!localStorage.getItem('hc_rota_pending') } catch {}
    if (!pending) return
    try { localStorage.removeItem('hc_rota_pending') } catch {}
    setAutoFired(true)
    ask()
  }, [text, crew.length]) // eslint-disable-line

  async function ask() {
    if (!text.trim()) return
    if (!crew.length) { alert('No crew aboard - nobody to put on duty.'); return }
    setBusy(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const resp = await fetch('/api/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + (session?.access_token || '') },
        body: JSON.stringify({ action: 'parse_rota', text, crew, today: shipTodayAuto() }),
      })
      const out = await resp.json()
      if (out.error) throw new Error(out.error)
      if (!out.dateFrom || !out.dateTo) throw new Error('Could not work out the dates from that. Try naming a month or a date range.')
      build(out)
    } catch (e) { alert('Could not read that: ' + e.message) }
    setBusy(false)
  }

  function build(sp) {
    const mentionsHOD = /head|hod|chief/i.test(text)
    const noCaptain = /no captain|not the captain|without the captain|except the captain/i.test(text)

    // Crew who join during the period are not in the Onboard list yet - they must
    // still be eligible. Build the pools from EVERYONE relevant to this rota.
    const joining = movements
      .filter(m => m.sign_on && !crew.some(c => c.name.toLowerCase() === (m.crew_name || '').toLowerCase()))
      .map(m => {
        const known = (allCrew || []).find(c => c.name.toLowerCase() === (m.crew_name || '').toLowerCase())
        return { id: 'mv-' + m.id, name: m.crew_name, role: m.role || (known ? known.role : '') }
      })
    const rotaCrew = [...crew, ...joining]

    if (mentionsHOD) {
      // Classify from the crew's actual roles. The model gets this wrong too often.
      const hodCrew = rotaCrew.filter(c => isHOD(c.role) && !isCaptain(c.role))
      const restCrew = rotaCrew.filter(c => !isHOD(c.role) && !isCaptain(c.role))
      sp = {
        ...sp,
        weekendPool: hodCrew.map(c => c.name),
        weekdayPool: restCrew.map(c => c.name),
        reasoning: 'Weekends: heads of department (' + hodCrew.map(c => c.role).join(', ') +
          '). Weekdays: the rest of the crew (' + restCrew.map(c => c.role).join(', ') + ').',
      }
    }
    if (noCaptain) {
      const captains = rotaCrew.filter(c => isCaptain(c.role)).map(c => c.name)
      sp = { ...sp,
        exclude: [...new Set([...(sp.exclude || []), ...captains])],
        weekdayPool: (sp.weekdayPool || []).filter(n => !captains.includes(n)),
        weekendPool: (sp.weekendPool || []).filter(n => !captains.includes(n)),
      }
    }
    // Resolve weekday-name pins (e.g. {day:'Saturday'}) to the actual date in range.
    if (Array.isArray(sp.pins) && sp.pins.length) {
      const DOW = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday']
      const inRange = []
      { const d = new Date(sp.dateFrom + 'T12:00:00'); const end = new Date(sp.dateTo + 'T12:00:00')
        while (d <= end) { const iso = d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0')+'-'+String(d.getDate()).padStart(2,'0'); inRange.push({ iso, dow: DOW[d.getDay()] }); d.setDate(d.getDate()+1) } }
      sp = { ...sp, pins: sp.pins.map(pn => {
        if (pn.date) return pn
        if (pn.day) { const hit = inRange.find(x => x.dow.toLowerCase() === String(pn.day).toLowerCase()); return hit ? { date: hit.iso, name: pn.name } : pn }
        return pn
      }).filter(pn => pn.date && pn.name) }
    }
    setSpec(sp)
    const abs = absencesFrom(movements, rotaCrew, sp.dateFrom, sp.dateTo)
    const r = generateRota(sp, rotaCrew, abs)
    setRota(r)
    setVerdict(verifyRota(r, sp, rotaCrew, abs))
  }

  function reassign(iso, name) {
    setRota(r => {
      const days = { ...r.days, [iso]: name ? { ...r.days[iso], name } : undefined }
      if (!name) delete days[iso]
      const next = { ...r, days }
      setVerdict(verifyRota(next, spec, crew, []))
      return next
    })
  }

  function exportPdf() {
    if (!rota || !spec) return
    const vessel = (config && config.vessel_name) || 'Vessel'
    const NAVY = '#0A1628', GOLD = '#C9A84C', LINE = '#E5E9F1', MUTE = '#67748C', TEXT = '#16233B', BLUE = '#1155F5'
    const wave = '<span style="display:inline-block;font-size:30px;font-weight:900;color:' + BLUE + ';line-height:1;letter-spacing:-3px;">\u2248</span>'
    const fmt = iso => new Date(iso + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    const sn = x => escHtml(shortName(x))   // crew names into the html: escaped (zap-active)

    const cells = weeks.flat().map(iso => {
      if (!iso) return '<td style="border:1px solid ' + LINE + ';height:52px;background:#FAFBFD;"></td>'
      const d = rota.days[iso]
      const wknd = d && d.weekend
      const mv = movesOn(iso)
      const mvHtml =
        (mv.off.length ? '<div style="font-size:7.5px;font-weight:700;color:#C93B47;line-height:1.3;">' + mv.off.map(sn).join(', ') + ' OFF</div>' : '') +
        (mv.on.length ? '<div style="font-size:7.5px;font-weight:700;color:#137050;line-height:1.3;">' + mv.on.map(sn).join(', ') + ' ON</div>' : '')
      return '<td style="border:1px solid ' + LINE + ';height:58px;padding:6px 8px;vertical-align:top;' +
        (wknd ? 'background:rgba(62,74,95,.05);' : '') + '">' +
        '<div style="font-size:10px;font-weight:700;color:' + NAVY + ';">' + Number(iso.slice(8)) + '</div>' +
        '<div style="font-size:10.5px;font-weight:600;color:' + TEXT + ';margin-top:3px;">' + sn(d && d.name) + '</div>' +
        mvHtml + '</td>'
    })
    const rowsHtml = []
    for (let i = 0; i < cells.length; i += 7) rowsHtml.push('<tr>' + cells.slice(i, i + 7).join('') + '</tr>')

    const tally = Object.entries(counts).sort((a, b) => b[1] - a[1])
      .map(([n, c]) => '<span style="font-size:10px;color:' + MUTE + ';margin-right:14px;">' + escHtml(n) + ' <b style="color:' + TEXT + ';">' + c + '</b></span>').join('')

    const html = '<div style="width:1000px;padding:8px 12px;font-family:Inter,Helvetica,Arial,sans-serif;color:' + TEXT + ';background:#fff;">' +
      '<div style="display:flex;justify-content:space-between;align-items:center;padding-bottom:10px;">' +
        '<div style="display:flex;align-items:center;gap:11px;">' + wave +
          '<span style="font-size:20px;font-weight:800;letter-spacing:.13em;color:' + NAVY + ';">HEYCAPTAIN</span></div>' +
        '<div style="font-size:15px;font-weight:700;color:' + TEXT + ';">' + escHtml(vessel) + '</div>' +
      '</div>' +
      '<div style="text-align:center;margin:2px 0 14px;">' +
        '<div style="font-size:11px;color:' + MUTE + ';letter-spacing:.28em;text-transform:uppercase;margin-bottom:4px;">Duty Rota</div>' +
        '<div style="font-size:22px;font-weight:800;color:' + NAVY + ';letter-spacing:.01em;">' + fmt(spec.dateFrom) + ' &ndash; ' + fmt(spec.dateTo) + '</div>' +
      '</div>' +
      '<div style="height:12px;"></div>' +
      '<div style="border-radius:12px;overflow:hidden;border:1px solid ' + LINE + ';">' +
      '<table style="width:100%;border-collapse:collapse;table-layout:fixed;">' +
        '<thead><tr>' + DOW.map(d => '<th style="width:14.28%;padding:7px 0;font-size:9px;letter-spacing:.1em;text-transform:uppercase;color:#fff;background:' +
          ((d === 'Sat' || d === 'Sun') ? '#3E4A5F' : NAVY) + ';">' + d + '</th>').join('') + '</tr></thead>' +
        '<tbody>' + rowsHtml.join('') + '</tbody></table></div>' +
      '<div style="margin-top:12px;">' + tally + '</div>' +
      (notes.length ? '<div style="margin-top:20px;display:flex;gap:22px;">' + notes.map(n =>
        '<div style="flex:1;">' +
          '<div style="font-size:10px;font-weight:700;color:#C93B47;margin-bottom:4px;">' + escHtml(n.title || '') + '</div>' +
          '<div style="font-size:8.5px;line-height:1.6;color:' + TEXT + ';">' + escHtml(n.body || '').replace(/\n/g, '<br/>') + '</div>' +
        '</div>').join('') + '</div>' : '') +
      '<div style="margin-top:16px;padding-top:9px;border-top:1px solid ' + LINE + ';display:flex;justify-content:space-between;font-size:8.5px;color:' + MUTE + ';">' +
        '<span style="font-weight:800;letter-spacing:.12em;color:' + NAVY + ';">HEYCAPTAIN</span>' +
        '<span>' + escHtml(vessel) + ' &middot; Duty Rota &middot; generated ' + fmt(todayLocal()) + '</span></div>' +
    '</div>'

    const el = document.createElement('div')
    el.innerHTML = html
    document.body.appendChild(el)
    const fname = ('Duty_Rota_' + vessel + '_' + spec.dateFrom).replace(/[^a-z0-9]+/gi, '_') + '.pdf'
    html2pdf().set({ margin: 8, filename: fname, image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, windowWidth: 1000, width: 1000 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' } })
      .from(el).save().then(() => document.body.removeChild(el))
  }

  async function save() {
    if (!rota || !spec) return
    const name = prompt('Rota name', 'Duty rota ' + spec.dateFrom)
    if (!name) return
    setSaving(true)
    const { error } = await supabase.from('duty_rotas').insert({
      client_id: config.id, name, date_from: spec.dateFrom, date_to: spec.dateTo,
      spec, days: rota.days, notes,
    })
    if (error) alert('Save failed: ' + error.message)
    else { await loadArchive(); alert('Saved.') }
    setSaving(false)
  }

  // crew changes falling on a given day
  const movesOn = (iso) => {
    const on = movements.filter(m => m.sign_on === iso).map(m => m.crew_name)
    const off = movements.filter(m => m.sign_off === iso).map(m => m.crew_name)
    return { on, off }
  }

  // calendar layout
  const weeks = []
  if (rota && spec) {
    const start = new Date(spec.dateFrom + 'T12:00:00')
    const end = new Date(spec.dateTo + 'T12:00:00')
    const lead = (start.getDay() + 6) % 7
    let row = new Array(lead).fill(null)
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const iso = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
      row.push(iso)
      if (row.length === 7) { weeks.push(row); row = [] }
    }
    if (row.length) { while (row.length < 7) row.push(null); weeks.push(row) }
  }

  const counts = {}
  if (rota) Object.values(rota.days).forEach(d => { if (d?.name) counts[d.name] = (counts[d.name] || 0) + 1 })

  return (
    <div style={{ padding: 20, maxWidth: 1100, margin: '0 auto' }}>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input value={text} onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') ask() }}
          placeholder='e.g. "June, weekday duty rotates through everyone, weekends heads of department only, no captain"'
          style={{ flex: 1, padding: '11px 14px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--card, #fff)', color: 'var(--text)', fontSize: 13 }} />
        <button onClick={ask} disabled={busy} style={{ padding: '11px 20px', borderRadius: 10, border: 'none', background: 'var(--gold, #C9A84C)', color: '#0A1628', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
          {busy ? 'Reading…' : 'Generate'}
        </button>
        <button onClick={() => setShowMoves(true)} style={{ padding: '11px 16px', borderRadius: 10, border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted)', fontSize: 13, cursor: 'pointer' }}>
          Crew changes{movements.length ? ' \u00b7 ' + movements.length : ''}
        </button>
        <button onClick={() => setShowArchive(true)} style={{ padding: '11px 16px', borderRadius: 10, border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted)', fontSize: 13, cursor: 'pointer' }}>Saved</button>
      </div>

      {spec && (
        <div style={{ padding: '12px 16px', borderRadius: 10, background: 'rgba(201,168,76,.08)', border: '1px solid var(--border)', marginBottom: 14, fontSize: 12.5, color: 'var(--text)', lineHeight: 1.8 }}>
          <div><b>{spec.dateFrom} → {spec.dateTo}</b></div>
          <div><span style={{ color: 'var(--muted)' }}>Weekdays:</span> {(spec.weekdayPool || []).join(', ') || '—'}</div>
          <div><span style={{ color: 'var(--muted)' }}>Weekends:</span> {(spec.weekendPool || []).join(', ') || '—'}</div>
          {(spec.exclude || []).length > 0 && <div><span style={{ color: 'var(--muted)' }}>Excluded:</span> {spec.exclude.join(', ')}</div>}
          {spec.reasoning && <div style={{ color: 'var(--muted)', fontStyle: 'italic', marginTop: 3 }}>{spec.reasoning}</div>}
        </div>
      )}

      {rota?.problems?.length > 0 && (
        <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(201,59,71,.09)', border: '1px solid rgba(201,59,71,.35)', color: '#C93B47', fontSize: 12.5, fontWeight: 600, marginBottom: 12 }}>
          {rota.problems.join(' · ')}
        </div>
      )}
      {verdict && !verdict.ok && (
        <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(201,59,71,.09)', border: '1px solid rgba(201,59,71,.35)', color: '#C93B47', fontSize: 12.5, marginBottom: 12 }}>
          {verdict.errors.map((e, i) => <div key={i}>{e}</div>)}
        </div>
      )}
      {verdict?.warnings?.length > 0 && (
        <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(185,125,20,.09)', border: '1px solid rgba(185,125,20,.3)', color: '#8a5c0a', fontSize: 12.5, marginBottom: 12 }}>
          {verdict.warnings.map((w, i) => <div key={i}>{w}</div>)}
        </div>
      )}

      {weeks.length > 0 && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6, marginBottom: 6 }}>
            {DOW.map(d => (
              <div key={d} style={{ padding: '7px 0', textAlign: 'center', fontSize: 10, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase',
                color: '#fff', background: (d === 'Sat' || d === 'Sun') ? '#3E4A5F' : '#0A1628', borderRadius: 7 }}>{d}</div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
            {weeks.flat().map((iso, i) => {
              if (!iso) return <div key={i} />
              const d = rota.days[iso]
              const wknd = d?.weekend
              return (
                <div key={iso} style={{ minHeight: 92, padding: '8px 9px', borderRadius: 10, border: '1px solid var(--border)',
                  background: wknd ? 'rgba(62,74,95,.05)' : 'var(--card, #fff)' }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text)' }}>{Number(iso.slice(8))}</div>
                  <select value={d?.name || ''} onChange={e => reassign(iso, e.target.value)}
                    style={{ width: '100%', marginTop: 4, padding: '4px 5px', fontSize: 11.5, fontWeight: 600, borderRadius: 6,
                      border: '1px solid transparent', background: 'transparent', color: d?.name ? 'var(--text)' : 'var(--faint, #98A3B8)', cursor: 'pointer' }}>
                    <option value="">—</option>
                    {(allCrew && allCrew.length ? allCrew : crew).map(c => <option key={c.id} value={c.name}>{shortName(c.name)}</option>)}
                    {d?.name && !(allCrew || crew).some(c => c.name === d.name) && <option value={d.name}>{shortName(d.name)}</option>}
                  </select>
                  {(() => {
                    const mv = movesOn(iso)
                    if (!mv.on.length && !mv.off.length) return null
                    return (
                      <div style={{ marginTop: 3 }}>
                        {mv.off.length > 0 && (
                          <div style={{ fontSize: 9, fontWeight: 700, color: '#C93B47', lineHeight: 1.35 }}>{mv.off.map(shortName).join(', ')} OFF</div>
                        )}
                        {mv.on.length > 0 && (
                          <div style={{ fontSize: 9, fontWeight: 700, color: '#137050', lineHeight: 1.35 }}>{mv.on.map(shortName).join(', ')} ON</div>
                        )}
                      </div>
                    )
                  })()}
                </div>
              )
            })}
          </div>

          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', marginTop: 16 }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', flex: 1 }}>
              {Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([n, c]) => (
                <span key={n} style={{ fontSize: 11.5, color: 'var(--muted)' }}>{n} <b style={{ color: 'var(--text)' }}>{c}</b></span>
              ))}
            </div>
            <button onClick={() => setNotes(n => [...n, { title: 'New note', body: '' }])} style={{ padding: '8px 14px', borderRadius: 9, fontSize: 12, cursor: 'pointer', border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted)' }}>+ Note</button>
            <button onClick={exportPdf} style={{ padding: '8px 14px', borderRadius: 9, fontSize: 12, cursor: 'pointer', border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted)' }}>Export PDF</button>
            <button onClick={() => build(spec)} style={{ padding: '8px 14px', borderRadius: 9, fontSize: 12, cursor: 'pointer', border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted)' }}>Regenerate</button>
            <button onClick={save} disabled={saving} style={{ padding: '8px 16px', borderRadius: 9, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', background: 'var(--gold, #C9A84C)', color: '#0A1628' }}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </>
      )}

      {rota && (
        <div style={{ marginTop: 22, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 16 }}>
          {notes.map((n, i) => (
            <div key={i} style={{ position: 'relative' }}>
              <input value={n.title}
                onChange={e => setNotes(ns => ns.map((x, j) => j === i ? { ...x, title: e.target.value } : x))}
                style={{ width: '100%', border: 'none', background: 'transparent', font: 'inherit', fontSize: 12.5, fontWeight: 700,
                  color: '#C93B47', marginBottom: 4, padding: 0, outline: 'none' }} />
              <textarea value={n.body}
                onChange={e => setNotes(ns => ns.map((x, j) => j === i ? { ...x, body: e.target.value } : x))}
                rows={5}
                style={{ width: '100%', border: '1px solid transparent', borderRadius: 7, background: 'transparent', font: 'inherit',
                  fontSize: 11.5, lineHeight: 1.55, color: 'var(--text)', padding: '4px 5px', resize: 'vertical', outline: 'none' }}
                onFocus={e => { e.target.style.border = '1px solid var(--border)'; e.target.style.background = 'var(--card, #fff)' }}
                onBlur={e => { e.target.style.border = '1px solid transparent'; e.target.style.background = 'transparent' }} />
              <button onClick={() => setNotes(ns => ns.filter((_, j) => j !== i))}
                style={{ position: 'absolute', top: 0, right: 0, background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 13 }}>&times;</button>
            </div>
          ))}
        </div>
      )}

      {showMoves && (
        <div onClick={() => setShowMoves(false)} style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(10,22,40,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--card, #fff)', borderRadius: 14, width: 640, maxWidth: '95%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 70px rgba(0,0,0,.35)' }}>
            <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>Crew changes</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>Who is joining and leaving, and when. The rota will not put anyone on duty before they join or after they leave.</div>
            </div>
            <div style={{ overflowY: 'auto', padding: '10px 20px' }}>
              {movements.map(m => (
                <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: '1px solid var(--border)', fontSize: 12.5 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: 'var(--text)' }}>{m.crew_name} <span style={{ fontWeight: 400, color: 'var(--muted)' }}>{m.role}</span></div>
                    <div style={{ color: 'var(--muted)', fontSize: 11.5 }}>
                      {m.sign_on ? 'joins ' + m.sign_on : 'aboard'}
                      {m.sign_off ? ' \u00b7 leaves ' + m.sign_off : ''}
                      {m.replacing ? ' \u00b7 relieving ' + m.replacing : ''}
                    </div>
                  </div>
                  <button onClick={() => delMovement(m)} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 14 }}>&times;</button>
                </div>
              ))}
              {!movements.length && <div style={{ padding: 16, fontSize: 12, color: 'var(--muted)' }}>No crew changes recorded.</div>}
            </div>
            <MovementForm crew={crew} allCrew={allCrew} onAdd={addMovement} />
          </div>
        </div>
      )}

      {showArchive && (
        <div onClick={() => setShowArchive(false)} style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(10,22,40,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--card, #fff)', borderRadius: 14, width: 520, maxWidth: '94%', maxHeight: '76vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 70px rgba(0,0,0,.35)' }}>
            <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid var(--border)', fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>Saved rotas</div>
            <div style={{ overflowY: 'auto', padding: '4px 20px 12px' }}>
              {archive.map(r => (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{r.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{r.date_from} → {r.date_to}</div>
                  </div>
                  <button onClick={() => {
                      let days = r.days, sp = r.spec
                      if (typeof days === 'string') { try { days = JSON.parse(days) } catch (e) { days = null } }
                      if (typeof sp === 'string') { try { sp = JSON.parse(sp) } catch (e) { sp = null } }
                      if (!days || !sp || !Object.keys(days).length) { alert('That rota could not be read back.'); return }
                      setSpec(sp)
                      setRota({ days, problems: [], stats: {} })
                      setVerdict(verifyRota({ days }, sp, crew, []))
                      let n = r.notes
                      if (typeof n === 'string') { try { n = JSON.parse(n) } catch (e) { n = null } }
                      setNotes(Array.isArray(n) && n.length ? n : DEFAULT_NOTES)
                      setShowArchive(false)
                    }}
                    style={{ fontSize: 11, color: 'var(--gold, #C9A84C)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}>Load</button>
                  <button onClick={async () => { if (confirm('Delete the saved rota "' + (r.name || 'rota') + '"? You can undo it for 10 seconds.')) { try { await deleteWithUndo(supabase, { table: 'duty_rotas', id: r.id, label: 'rota "' + (r.name || 'rota') + '"', onUndone: () => loadArchive() }) } catch (e) { alert('Could not delete: ' + (e.message || e)) } loadArchive() } }}
                    style={{ fontSize: 13, color: 'var(--muted)', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
                </div>
              ))}
              {!archive.length && <div style={{ padding: 20, fontSize: 12, color: 'var(--muted)' }}>Nothing saved yet.</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function MovementForm({ crew, allCrew, onAdd }) {
  const [name, setName] = useState('')
  const [role, setRole] = useState('')
  const [on, setOn] = useState('')
  const [off, setOff] = useState('')
  const [replacing, setReplacing] = useState('')

  const inp = { padding: '7px 9px', borderRadius: 7, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)', fontSize: 12 }

  return (
    <div style={{ padding: '14px 20px', borderTop: '1px solid var(--border)', display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center' }}>
      <input list="hc-crew" value={name} onChange={e => {
          setName(e.target.value)
          const c = (allCrew || []).find(x => x.name === e.target.value)
          if (c) setRole(c.role)
        }} placeholder="Crew name" style={{ ...inp, width: 150 }} />
      <datalist id="hc-crew">{(allCrew || []).map(c => <option key={c.id} value={c.name} />)}</datalist>
      <input value={role} onChange={e => setRole(e.target.value)} placeholder="Role" style={{ ...inp, width: 120 }} />
      <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--muted)' }}>joins
        <DateField value={on} onChange={v => setOn(v || '')} style={{ ...inp, width: 128 }} /></span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--muted)' }}>leaves
        <DateField value={off} onChange={v => setOff(v || '')} style={{ ...inp, width: 128 }} /></span>
      <select value={replacing} onChange={e => setReplacing(e.target.value)} style={{ ...inp, width: 150 }}>
        <option value="">Relieving…</option>
        {(allCrew || []).map(c => <option key={c.id} value={c.name}>{c.name}{c.onboard ? '' : ' (ashore)'}</option>)}
      </select>
      <button onClick={() => {
          if (!name.trim()) { alert('Name?'); return }
          if (!on && !off) { alert('Give a joining or leaving date.'); return }
          onAdd({ crew_name: name.trim(), role: role.trim() || null, sign_on: on || null, sign_off: off || null, replacing: replacing.trim() || null })
          setName(''); setRole(''); setOn(''); setOff(''); setReplacing('')
        }}
        style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: 'var(--gold, #C9A84C)', color: '#0A1628', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>Add</button>
    </div>
  )
}
